import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Download, FileText, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useBrandData } from '../../hooks/useBrandData';

export default function BrandSummary() {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const { data } = useBrandData();
  const meta = data?.meta;
  const updatedLabel = meta?.updated
    ? new Date(meta.updated).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : '';
  
  const handleDownloadPDF = async () => {
    setIsGenerating(true);
    toast.loading('Generating PDF guide...');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate-brand-pdf`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'jackstone-brand-style-guide.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('Brand Style Guide PDF downloaded!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.dismiss();
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Quick Reference Summary
        </h1>
        <p className="text-base text-body leading-relaxed">
          Essential brand guidelines at a glance for quick reference and daily use.
        </p>
      </div>

      <Card className="p-8 bg-gradient-to-br from-jackstone-blue to-navy text-white">
        <img src="/logo-white.png" alt="Jackstone" className="h-9 mb-5" />
        <p className="text-2xl md:text-3xl font-display font-normal mb-2 tracking-wide">Frozen Today, Fresh Tomorrow</p>
        <p className="text-white/80">Industrial freezing systems since 1937</p>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
            Brand Personality
          </h3>
          <div className="flex flex-wrap gap-2">
            <Badge>Expert & Knowledgeable</Badge>
            <Badge>Direct & Clear</Badge>
            <Badge>Reliable & Assuring</Badge>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
            Primary Brand Colors
          </h3>
          <div className="flex flex-wrap gap-3">
            <div>
              <div className="w-12 h-12 rounded bg-[#39678F] mb-1 border"></div>
              <p className="text-xs font-mono text-center">#39678F</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded bg-[#33424F] mb-1 border"></div>
              <p className="text-xs font-mono text-center">#33424F</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded bg-[#CCB795] mb-1 border"></div>
              <p className="text-xs font-mono text-center">#CCB795</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded bg-[#E6F3FF] mb-1 border"></div>
              <p className="text-xs font-mono text-center">#E6F3FF</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded bg-[#E6E0DB] mb-1 border"></div>
              <p className="text-xs font-mono text-center">#E6E0DB</p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Complete Color System
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <h4 className="font-medium text-body mb-3 text-sm">Neutral & Text</h4>
            <div className="flex gap-2">
              <div>
                <div className="w-12 h-12 rounded bg-[#565656] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#565656</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded bg-[#808080] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#808080</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded bg-[#A6A6A6] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#A6A6A6</p>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-body mb-3 text-sm">Background</h4>
            <div className="flex gap-2">
              <div>
                <div className="w-12 h-12 rounded bg-[#FFFFFF] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#FFFFFF</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded bg-[#E8E8E8] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#E8E8E8</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <h4 className="font-medium text-body mb-3 text-sm">Accent & Graph Colors</h4>
            <div className="flex flex-wrap gap-2">
              <div>
                <div className="w-12 h-12 rounded bg-[#B30035] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#B30035</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded bg-[#1997BD] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#1997BD</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded bg-[#4CD964] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#4CD964</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded bg-[#50C9B3] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#50C9B3</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded bg-[#4B4B9D] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#4B4B9D</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded bg-[#7B8D9E] border"></div>
                <p className="text-xs font-mono mt-1 text-center">#7B8D9E</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-surface">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Typography
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <p className="font-display text-2xl font-normal mb-2 uppercase">BEBAS NEUE</p>
            <p className="text-sm text-subtle">H1/Display headings</p>
          </div>
          <div>
            <p className="font-heading text-xl font-semibold mb-2">Montserrat Semibold</p>
            <p className="text-sm text-subtle">H2-H4 subheadings</p>
          </div>
          <div>
            <p className="font-sans text-lg mb-2" style={{fontWeight: 500}}>Montserrat Medium</p>
            <p className="text-sm text-subtle">Body text</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Logo Guidelines
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-success flex-shrink-0" />
              <p className="text-body">Clear space = height of letter 'J'</p>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-success flex-shrink-0" />
              <p className="text-body">Minimum width: 120px digital, 1 inch print</p>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-success flex-shrink-0" />
              <p className="text-body">Use primary blue or white versions only</p>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <X className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-body">Never distort, rotate, or add effects</p>
            </div>
            <div className="flex items-center gap-2">
              <X className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-body">Never change colors or proportions</p>
            </div>
            <div className="flex items-center gap-2">
              <X className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-body">Never place on busy backgrounds</p>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-surface">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Key Brand Principles
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="font-medium text-body">Visual</h4>
            <ul className="text-sm text-subtle space-y-1">
              <li>• Clean, technical aesthetic</li>
              <li>• Generous whitespace</li>
              <li>• High-quality imagery</li>
              <li>• Consistent grid system</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-body">Voice</h4>
            <ul className="text-sm text-subtle space-y-1">
              <li>• Technical but accessible</li>
              <li>• Direct and confident</li>
              <li>• Professional tone</li>
              <li>• Outcome-focused</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Brand Standards Checklist
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 border-2 border-jackstone-blue rounded mt-0.5 flex-shrink-0"></div>
              <p className="text-body">Logo used correctly with proper clear space</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 border-2 border-jackstone-blue rounded mt-0.5 flex-shrink-0"></div>
              <p className="text-body">Brand colors from approved palette only</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 border-2 border-jackstone-blue rounded mt-0.5 flex-shrink-0"></div>
              <p className="text-body">Typography: Bebas Neue + Montserrat</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 border-2 border-jackstone-blue rounded mt-0.5 flex-shrink-0"></div>
              <p className="text-body">Imagery is high-quality and on-brand</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 border-2 border-jackstone-blue rounded mt-0.5 flex-shrink-0"></div>
              <p className="text-body">Tone of voice is expert and reliable</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 border-2 border-jackstone-blue rounded mt-0.5 flex-shrink-0"></div>
              <p className="text-body">Layout uses consistent spacing</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 border-2 border-jackstone-blue rounded mt-0.5 flex-shrink-0"></div>
              <p className="text-body">Accessibility standards met (WCAG AA)</p>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6 bg-light-blue border-jackstone-blue/20">
          <h3 className="text-xl font-heading font-semibold text-body mb-3">
            Download Guide
          </h3>
          <p className="text-body mb-4 text-sm">
            Get the complete Brand Style Guide as a print-ready A4 PDF document.
          </p>
          <Button onClick={handleDownloadPDF} className="gap-2 w-full" disabled={isGenerating}>
            <Download className="h-4 w-4" />
            {isGenerating ? 'Generating PDF...' : 'Download Brand Guide PDF'}
          </Button>
        </Card>

        <Card className="p-6 bg-surface">
          <h3 className="text-xl font-heading font-semibold text-body mb-3">
            Document Templates
          </h3>
          <p className="text-body mb-4 text-sm">
            Access branded templates for reports, presentations, and more.
          </p>
          <Link to="/templates">
            <Button variant="outline" className="gap-2 w-full">
              <FileText className="h-4 w-4" />
              View Templates Library
            </Button>
          </Link>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-3">
          Need Help?
        </h3>
        <p className="text-body mb-4">
          For questions about brand guidelines, asset requests, or approval on new materials:
        </p>
        <div className="space-y-2 text-sm text-subtle">
          <p>• Contact: marketing@jackstone.com</p>
          <p>• Brand Asset Library: [Internal Link]</p>
          <p>• Submit materials for review before production</p>
        </div>
      </Card>

      <div className="text-center py-8">
        <p className="text-sm text-subtle">
          Last updated: {updatedLabel || '—'} | Version {meta?.version || '—'}
        </p>
      </div>
    </div>
  );
}
